import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Searchbar, Statusindicator, Table, Theme } from '../../components';
import { FetchCrudData, FoodType, FoodTypeField, GetTableDataOptions, SORT_ORDER, TableColumn } from '../../models';
import { foodTypeActions, foodTypeSelectors } from '../../redux';
import { ITEMS_PER_PAGE } from '../constants';
import DeleteFoodTypeModal from './components/DeleteFoodTypeModal';
import EditWarningTimerModal from './components/EditWarningTimerModal';
import FoodTypeModal from './components/FoodTypeModal';

import './admin.scss';

export const GET_FOOD_TYPE_COLUMNS = (): TableColumn[] => [
  {
    label: 'lblFoodItem',
    field: FoodTypeField.NAME,
  },
  {
    label: 'lblMaxHoldingTime',
    field: FoodTypeField.MAX_HOLD_TIME,
  },
  {
    label: 'lblActive',
    field: FoodTypeField.IS_ACTIVE,
  },
  {
    label: 'lblArticleNumber',
    field: FoodTypeField.OCIS_ID,
  },
  {
    label: '',
    field: 'actions',
    sortable: false,
  },
];

enum foodTypeModal {
  CHANGE_WARNING = 'CHANGE_WARNING',
  DELETE_FOOD_TYPE = 'DELETE_FOOD_TYPE',
  ADD_EDIT_FOOD_TYPE = 'ADD_FOOD_TYPE',
}

const AdminFoodTypes = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  const foodTypes = useSelector(foodTypeSelectors.getFoodTypes);
  const isLoading = useSelector(foodTypeSelectors.isLoading);
  const isDeleteLoading = useSelector(foodTypeSelectors.isDeleteLoading);
  const isAddEditLoading = useSelector(foodTypeSelectors.isAddEditLoading);
  const total = useSelector(foodTypeSelectors.getTotal);

  const [openedModal, setOpenedModal] = useState<{ type: foodTypeModal; id?: string }>();
  const [searchValue, setSearchValue] = useState<string>('');
  const [filters, setFilters] = useState<FetchCrudData>({
    offset: 0,
    limit: ITEMS_PER_PAGE,
    sort: [`${FoodTypeField.NAME}:${i18n.language},${SORT_ORDER.ASC}`],
  });

  useEffect(() => {
    if (filters.offset === 0) {
      dispatch(foodTypeActions.getFoodTypes(filters));
    } else if (filters.offset < total) {
      dispatch(foodTypeActions.getMoreFoodTypes(filters));
    }
  }, [filters]);

  useEffect(() => {
    if (!isDeleteLoading && !isAddEditLoading) {
      setFilters(prevState => ({ ...prevState, offset: 0 }));
      window.scrollTo(0, 0);
    }
  }, [isDeleteLoading, isAddEditLoading]);

  const renderTableCell = (value: unknown, field: string, obj: FoodType) => {
    switch (field) {
      case FoodTypeField.NAME:
        return obj.translatedLabels.find(({ languageCode }) => languageCode === i18n.language)?.translation;
      case FoodTypeField.IS_ACTIVE:
        return <Statusindicator status={value as boolean} />;
      case FoodTypeField.MAX_HOLD_TIME:
        return value + ' ' + t('lblMinutes');
      case 'actions':
        return (
          <>
            <Button
              theme={Theme.TRANSPARANT}
              icon='Edit'
              onClickCallback={() =>
                setOpenedModal({
                  type: foodTypeModal.ADD_EDIT_FOOD_TYPE,
                  id: (obj as { id: string }).id,
                })
              }
            />
            <Button
              theme={Theme.TRANSPARANT}
              icon='Trashcan'
              onClickCallback={() =>
                setOpenedModal({
                  type: foodTypeModal.DELETE_FOOD_TYPE,
                  id: (obj as { id: string }).id,
                })
              }
            />
          </>
        );
      default:
        return value || '-';
    }
  };

  const getData = (options: GetTableDataOptions) => {
    setFilters({
      ...filters,
      offset: Math.floor(options.page * ITEMS_PER_PAGE),
      sort: [`${options.sortColumn}${options.sortColumn === FoodTypeField.NAME ? ':' + i18n.language : ''},${options.sortOrder}`],
    });
  };

  return (
    <div className='content'>
      <div className='sticky-header'>
        <div className='top-buttons'>
          <Searchbar
            placeholder={t('lblSearchFoodItem')}
            value={searchValue}
            onEnter={() => setFilters(prevState => ({ ...prevState, s: searchValue }))}
            onChange={setSearchValue}
          />
          <Button
            theme={Theme.SECONDARY}
            onClickCallback={() => setOpenedModal({ type: foodTypeModal.CHANGE_WARNING })}
            icon='Warning'>
            {t('lblChangeWarning')}
          </Button>
          <Button
            theme={Theme.SECONDARY}
            onClickCallback={() => setOpenedModal({ type: foodTypeModal.ADD_EDIT_FOOD_TYPE, id: '' })}
            icon='Add'>
            {t('lblAddFoodItem')}
          </Button>
        </div>
        <h1 className='title'>{t('titleFoodTypes')}</h1>
      </div>
      <section>
        <Table
          columns={GET_FOOD_TYPE_COLUMNS()}
          renderTableCell={renderTableCell}
          data={foodTypes}
          getData={getData}
          page={Math.floor(filters.offset / ITEMS_PER_PAGE)}
          isLoading={isLoading}
          defaultSortColumn={FoodTypeField.NAME}
          defaultSortOrder={SORT_ORDER.ASC}
        />
        <EditWarningTimerModal
          isOpen={openedModal?.type === foodTypeModal.CHANGE_WARNING}
          closeModal={() => setOpenedModal(null)}
        />
        <FoodTypeModal
          isOpen={openedModal?.type === foodTypeModal.ADD_EDIT_FOOD_TYPE}
          closeModal={() => setOpenedModal(null)}
          id={openedModal?.id}
        />
        <DeleteFoodTypeModal
          isOpen={openedModal?.type === foodTypeModal.DELETE_FOOD_TYPE}
          closeModal={() => setOpenedModal(null)}
          id={openedModal?.id}
        />
      </section>
    </div>
  );
};

export default AdminFoodTypes;
